import {
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  writeBatch,
} from 'firebase/firestore';
import { getLocalizedError } from '../i18n/errorCopy';
import { auth, db } from '../firebase';

export const ADMIN_EMAIL = 'pe.lipovsky@gmail.com';

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

async function readAccountDocuments(uid) {
  const [userDocument, tellerDocument] = await Promise.all([
    getDoc(doc(db, 'users', uid)).catch(() => null),
    getDoc(doc(db, 'fortuneTellers', uid)).catch(() => null),
  ]);
  return {
    userData: userDocument?.exists() ? userDocument.data() : null,
    tellerData: tellerDocument?.exists() ? tellerDocument.data() : null,
  };
}

export async function resolveAccountRole(user) {
  if (!user?.uid) return null;
  if (String(user.email || '').trim().toLowerCase() === ADMIN_EMAIL) return 'admin';
  const { userData, tellerData } = await readAccountDocuments(user.uid);
  return tellerData?.role === 'teller'
    || tellerData?.type === 'fortuneTeller'
    || userData?.role === 'teller'
    || userData?.type === 'fortuneTeller'
    ? 'teller'
    : 'user';
}

export async function ensureAccountProfile(user, requestedRole = 'user', profileName = '') {
  if (!user?.uid) throw new Error('The authenticated account could not be loaded.');
  if (String(user.email || '').trim().toLowerCase() === ADMIN_EMAIL) {
    return { role: 'admin', approvalStatus: null, created: false };
  }

  const { userData, tellerData } = await readAccountDocuments(user.uid);
  if (userData || tellerData) {
    const role = await resolveAccountRole(user);
    return {
      role,
      approvalStatus: role === 'teller'
        ? (tellerData?.approvalStatus || (tellerData?.approved === true ? 'approved' : 'draft'))
        : null,
      created: false,
    };
  }

  const role = requestedRole === 'teller' ? 'teller' : 'user';
  const now = new Date().toISOString();
  const fallbackName = String(user.email || '').split('@')[0] || 'Fate Room member';
  const name = String(profileName || user.displayName || fallbackName).trim().slice(0, 80);
  const photoUrl = String(user.photoURL || '');
  const baseProfile = {
    id: user.uid,
    name,
    email: String(user.email || ''),
    role,
    subscriptionPlan: 'free',
    tellerSubscriptionPlan: 'free',
    favorites: [],
    bookingHistory: [],
    notifications: [],
    photoUrl,
    bio: '',
    locale: 'en',
    createdAt: now,
    updatedAt: now,
  };

  const batch = writeBatch(db);
  batch.set(doc(db, 'users', user.uid), baseProfile);

  if (role === 'teller') {
    batch.set(doc(db, 'fortuneTellers', user.uid), {
      userId: user.uid,
      name,
      email: String(user.email || ''),
      tutorEmail: String(user.email || ''),
      profileImage: photoUrl,
      photoUrl,
      bio: '',
      description: '',
      skills: ['Tarot'],
      category: ['Tarot'],
      languages: ['English'],
      price: 30,
      rating: 0,
      reviewsCount: 0,
      role: 'teller',
      type: 'fortuneTeller',
      approved: false,
      banned: false,
      approvalStatus: 'draft',
      profileComplete: false,
      applicationCreatedAt: now,
      createdAt: now,
      updatedAt: now,
    });
  }

  await batch.commit();
  return {
    role,
    approvalStatus: role === 'teller' ? 'draft' : null,
    created: true,
  };
}

export async function continueWithGoogle(requestedRole = 'user', profileName = '') {
  const credential = await signInWithPopup(auth, googleProvider);
  const account = await ensureAccountProfile(credential.user, requestedRole, profileName);
  return {
    user: credential.user,
    ...account,
  };
}

export function authErrorMessage(error, language = 'en') {
  return getLocalizedError(error, language);
}
